import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { terminateDecomUsers, UserKeys } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import SharedDialog from '../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../../utils/roles/client-roles';
import { DecomSuspendedUserModel } from '../../../../../types';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface TerminateSuspendedUsersDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedRows: string[];
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
  userList: DecomSuspendedUserModel[];
}

export default function TerminateSuspendedUsersDialog({
  open,
  handleClose,
  selectedRows,
  setSelectedRows,
  changeLogRoles,
  userList,
}: TerminateSuspendedUsersDialogProps) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const { mutate, isPending } = useMutation({
    mutationFn: () => terminateDecomUsers(selectedModels, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message:
          selectedRows.length === 1
            ? 'The selected user has been terminated from decom.'
            : 'The selected users have been terminated from decom.',
      });
      const updatedModels: DecomSuspendedUserModel[] = selectedModels.map(
        (model) => {
          return {
            ...model,
            isactive: false,
            isTerminated: true,
          };
        }
      );
      const filteredList = userList.filter(
        (user) => !selectedRows.includes(user.userId + user.creationDate)
      );
      const updatedList = [...filteredList, ...updatedModels];
      queryClient.setQueryData([UserKeys.SuspendedUsers], updatedList);
      handleClose();
      setSelectedRows([]);
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          selectedRows.length === 1
            ? 'There was an issue terminating the selected user from decom. Please refresh and try again.'
            : 'There was an issue terminating the selected users from decom. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        analytics.automation.decommissioning.terminate_suspended_users,
        {
          module: ModuleList.modules.automation.name,
          section: ModuleList.modules.automation.sections.decommissioning,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        ToolsRoles.TerminateDecomForUser.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  const selectedModels = userList.filter((user) =>
    selectedRows.includes(user.userId + user.creationDate)
  );

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={`Terminate Decommissioning ${
        selectedRows.length === 1 ? 'User' : 'Users'
      }`}
      dialogContent={
        <Stack gap={2}>
          {selectedRows.length > 1 && (
            <Typography>
              Terminate decommissioning process for{' '}
              <strong>{selectedRows.length} users</strong>? This will leave the
              users in their current state, no more operations will be taken on
              the users by the system, and all logs for completed operations
              will be preserved.
            </Typography>
          )}
          {selectedRows.length === 1 && (
            <Typography>
              Terminate decommissioning process for{' '}
              <strong>{selectedModels[0].email}</strong>? This will leave the
              user in their current state, no more operations will be taken on
              the user by the system, and all logs for completed operations will
              be preserved.
            </Typography>
          )}

          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        title: `Terminate ${selectedRows.length === 1 ? 'User' : 'Users'}`,
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'warning',
      }}
    />
  );
}
